import React, { useState, useEffect } from 'react';
import { useParams, Link, useLocation, useNavigate } from 'react-router-dom';
import { getProductByCode } from '../services/api';
import { useCart } from '../context/CartContext';
import { getImageUrl, createFallbackImage } from '../utils/imageUtils';
import './ProductPage.css';

const ProductPage = () => {
  const { code } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const { addToCart } = useCart();

  // Carregar detalhes do produto
  useEffect(() => {
    const fetchProduct = async () => {
      try {
        setLoading(true);
        const data = await getProductByCode(code);
        setProduct(data);
        setError(null);
      } catch (err) {
        console.error('Erro ao carregar produto:', err);
        setError('Não foi possível carregar os detalhes do produto. Por favor, tente novamente mais tarde.');
      } finally {
        setLoading(false);
      }
    };

    if (code) {
      fetchProduct();
    }
  }, [code]);
  
  // Rolar para o topo da página quando o componente é montado
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Formatar preço em reais
  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  };

  // Renderizar mensagem de carregamento
  if (loading) {
    return <div className="loading">Carregando detalhes do produto...</div>;
  }

  // Renderizar mensagem de erro
  if (error) {
    return <div className="error">{error}</div>;
  }

  // Renderizar mensagem de produto não encontrado
  if (!product) {
    return (
      <div className="container">
        <div className="product-not-found">
          <h2>Produto não encontrado</h2>
          <p>O produto que você está procurando não foi encontrado.</p>
          <Link to="/" className="back-button">Voltar para a página inicial</Link>
        </div>
      </div>
    );
  }

  // Lógica de Preço
  const originalPrice = product.price !== null && product.price !== undefined ? parseFloat(product.price) : null;
  const salePriceValue = product.salePrice !== null && product.salePrice !== undefined ? parseFloat(product.salePrice) : null;

  let showDiscount = false;
  if (typeof salePriceValue === 'number' && !isNaN(salePriceValue) &&
      typeof originalPrice === 'number' && !isNaN(originalPrice) &&
      salePriceValue > 0 && // Preço promocional deve ser maior que 0
      salePriceValue < originalPrice) {
    showDiscount = true;
  }

  return (
    <div className="container">
      <div className="product-page">
        <div className="product-breadcrumb">
          <Link to={location.search ? `/?${location.search.slice(1)}` : '/'}>Início</Link> &gt; 
          <Link to={`/categoria/${encodeURIComponent(product.category)}`}>{product.category}</Link> &gt; 
          <span>{product.name}</span>
        </div>
        
        <div className="product-details">
          <div className="product-image-gallery">
            {product.code ? (
              <img 
                src={getImageUrl(`/imagens/${product.code}.png`)} 
                alt={product.name} 
                className="product-detail-image"
                onError={(e) => {
                  e.target.onerror = null;
                  // Criar imagem de fallback com o nome do produto
                  e.target.src = createFallbackImage(product.name, 300, 300);
                }}
                loading="lazy"
              />
            ) : (
              <div className="product-detail-image-placeholder">
                <span>Sem imagem</span>
              </div>
            )}
          </div>
          
          <div className="product-info-container">
            <h1 className="product-detail-name">{product.name}</h1>
            
            <div className="product-detail-code">Código: {product.code}</div>
            
            <div className="product-detail-category">
              <Link to={`/categoria/${encodeURIComponent(product.category)}`}>
                {product.category}
              </Link>
            </div>
            
            <div className="product-detail-price-container">
              {showDiscount ? (
                <>
                  <div className="product-detail-original-price">
                    De: <span>{formatPrice(originalPrice)}</span>
                  </div>
                  <div className="product-detail-sale-price">
                    Por: <span>{formatPrice(salePriceValue)}</span>
                  </div>
                  <div className="product-detail-discount">
                    {Math.round(((originalPrice - salePriceValue) / originalPrice) * 100)}% de desconto
                  </div>
                </>
              ) : (
                <div className="product-detail-price">
                  <span>{formatPrice(originalPrice)}</span>
                </div>
              )}
            </div>
            
            <div className={`product-detail-stock ${product.stock > 0 ? 'in-stock' : 'out-of-stock'}`}>
              {product.stock > 0 ? (
                <span>Em estoque: {product.stock} unidades</span>
              ) : (
                <span>Produto fora de estoque</span>
              )}
            </div>
            
            {product.stock > 0 && (
              <div className="product-detail-add-to-cart">
                <div className="quantity-controls">
                  <button 
                    onClick={() => setQuantity(prev => Math.max(1, prev - 1))}
                    disabled={quantity <= 1}
                  >
                    -
                  </button>
                  <span>{quantity}</span>
                  <button 
                    onClick={() => setQuantity(prev => Math.min(product.stock, prev + 1))}
                    disabled={quantity >= product.stock}
                  >
                    +
                  </button>
                </div>
                <button 
                  className="add-to-cart-button"
                  onClick={() => {
                    addToCart(product, quantity);
                    setQuantity(1);
                  }}
                >
                  <i className="fas fa-shopping-cart"></i>
                  Adicionar ao Carrinho
                </button>
              </div>
            )}
            
            {product.description && (
              <div className="product-detail-description">
                <h2>Descrição</h2>
                <div className="product-detail-description-content">
                  {product.description}
                </div>
              </div>
            )}
          </div>
        </div>
        
        <div className="product-actions">
          <button className="back-button" onClick={() => navigate('/')} style={{ marginRight: '10px' }}>
            Voltar para página principal
          </button>
          {location.search && (
            <Link to={`/?${location.search.slice(1)}`} className="results-button" style={{ marginRight: '10px' }}>
              Voltar aos resultados
            </Link>
          )}
          <Link to="/" className="home-button">Ir para a página inicial</Link>
        </div>
      </div>
    </div>
  );
};

export default ProductPage;
